// Client for the owner-only sharing endpoint (CONTRACTS §2 — POST /access).
// Writes to docs/* are server-side only (firestore.rules denies client writes),
// so the Share UI calls this function instead of the Firestore SDK. The
// __session cookie rides along automatically on the same-origin request.

import type { Role } from "./types";

export interface AccessState {
  viewers: string[];
  editors: string[];
  members: string[];
}

export interface AccessChange {
  add?: { email: string; role: Role }[];
  remove?: string[];
}

/** Apply a sharing change; resolves with the doc's new viewers/editors/members. */
export async function setAccess(
  docId: string,
  change: AccessChange
): Promise<AccessState> {
  const res = await fetch("/access", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ docId, ...change }),
    credentials: "same-origin",
  });

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Your session expired — sign in again.");
    }
    if (res.status === 403) {
      throw new Error("Only the document owner can change sharing.");
    }
    let message = `Sharing update failed (${res.status}).`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }

  return (await res.json()) as AccessState;
}
